import { useNavigate, useSearch } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import type { TaskPageResponse } from '@erp/rpc';
import { useTaskTypes } from 'src/api/rpc/hooks/tasks/useTaskTypes';
import { TaskMetricCards } from 'src/components/tasks/TaskMetricCards';
import { TaskAutomationStatStrip } from '../../tasks/TaskAutomationStatStrip';
import { TasksQueueToolbar } from '../../tasks/TasksQueueToolbar';
import { TaskQueueView } from '../../tasks/TaskQueueView';
import { useTaskQueueController } from '../../tasks/useTasksQueuePage';
import { buildTaskPageQueueColumns } from '../../tasks/columns/taskPageColumns';
import { isPathColumn } from '../../tasks/columns/taskColumnOptions';
import { TaskColumnPicker } from '../../tasks/columns/TaskColumnPicker';
import { TaskPageHeading } from './TaskPageHeading';
import { TaskPageTabStrip } from './TaskPageTabStrip';
import {
  taskPageQueueTableId,
  withActiveTab,
  type TaskPageTab,
} from './taskPageTabs';

/**
 * The tasks-queue experience pinned to one of the page's task types (the
 * active tab). URL search params still drive status/assignee/sort/pagination,
 * seeded from the page's `defaultFilters` when the URL carries none.
 * `tabs` is null on a single-type page (no tab strip, pre-tabs URL and
 * column key).
 */
export function TaskPageQueue({
  page,
  tab,
  tabs,
}: {
  page: TaskPageResponse;
  tab: TaskPageTab;
  tabs: readonly TaskPageTab[] | null;
}) {
  const gt = useGT();
  const searchParams = useSearch({
    from: '/authenticated/pages/$taskPageId',
  });
  const navigate = useNavigate();
  const multiType = tabs !== null;
  // The tab's admin defaults win over the built-in set; a user's saved
  // column config still overrides both.
  const defaultColumns =
    tab.defaultColumns ??
    buildTaskPageQueueColumns(
      gt('Anchor', {
        context:
          'task queue table column header for the business entity a task is working on',
      }),
    );
  const { data: taskTypes } = useTaskTypes();
  const taskType = taskTypes?.find((type) => type.id === tab.taskTypeId);

  const queue = useTaskQueueController({
    searchParams,
    navigateReplace: (search) => {
      void navigate({
        to: '/pages/$taskPageId',
        params: { taskPageId: page.id },
        search: withActiveTab(search, tab.key, multiType),
        replace: true,
      });
    },
    tableId: taskPageQueueTableId(page.id, tab.key, multiType),
    defaultColumns,
    forcedType: { id: tab.taskTypeId, key: tab.key, name: tab.typeName },
    seedFilters: page.defaultFilters,
    persistFilters: false,
    fromPageId: page.id,
    anchorEntityType: taskType?.anchorEntityType ?? null,
    // Users' saved columns pick up new admin defaults; a page that locks its
    // columns shows the defaults to everyone.
    savedColumns: page.allowUserColumnCustomization
      ? 'addNewDefaults'
      : 'ignore',
  });

  return (
    <TaskQueueView
      queue={queue}
      toolbar={
        <>
          <TasksQueueToolbar
            total={queue.total}
            searchInput={queue.searchInput}
            onSearchInputChange={queue.setSearchInput}
            searchParams={queue.searchParams}
            updateSearchParams={queue.updateSearchParams}
            taskTypes={queue.taskTypes}
            selectedTypeId={queue.selectedType?.id}
            users={queue.assigneePickerUsers}
            agents={queue.assigneePickerAgents}
            configuredColumns={queue.configuredColumns}
            onToggleColumn={(id, hidden) => queue.updateColumn(id, { hidden })}
            onResetColumns={queue.resetToDefaults}
            heading={<TaskPageHeading page={page} />}
            hideTypeFilter
            hideColumnsMenu={!page.allowUserColumnCustomization}
            columnPicker={
              <TaskColumnPicker taskType={taskType} onAdd={queue.addColumn} />
            }
            onRemoveColumn={{
              canRemove: isPathColumn,
              remove: queue.removeColumn,
            }}
            referenceColumns={queue.referenceColumns}
          />
          {/* Tabs and cards get more room than the dense toolbar rows; a
              single-type page with no cards renders nothing here. */}
          <div className="flex flex-col gap-4 py-2 empty:hidden">
            {tabs && (
              <TaskPageTabStrip page={page} tabs={tabs} activeKey={tab.key} />
            )}
            <TaskMetricCards
              taskTypeId={tab.taskTypeId}
              taskType={taskType}
              excludeSnoozed={!queue.showsSnoozed}
            />
          </div>
          <TaskAutomationStatStrip taskTypeKey={tab.key} />
        </>
      }
    />
  );
}
